/**
 * @module i18n
 * @description Centralized custom i18n system substituting Intlayer.
 * Exposes a writable `locale` store, a derived `t` store for translations,
 * and server-side utilities.
 */
import { writable, derived, get } from "svelte/store";
import { page } from "$app/state";
import { languages, type Language } from "$lib/lang";
import {
  type LocaleCode,
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  deepMerge,
  resolveKey,
  resolveKeyArr,
} from "./helpers";
import sharedTranslations from "./locales/shared.json";
import deDETranslations from "./locales/de-DE.json";
import enUSTranslations from "./locales/en-US.json";

// Cache for merged dictionaries to avoid re-merging on every access
const mergedDictionaries: Record<string, Record<string, any>> = {};

/**
 * Gets the fully merged translation dictionary for a locale.
 * Merges `shared.json` and the locale-specific JSON.
 */
export function getDictionary(locale: LocaleCode): Record<string, any> {
  if (mergedDictionaries[locale]) {
    return mergedDictionaries[locale];
  }

  let override = deDETranslations;
  if (locale === "en-US") {
    override = enUSTranslations;
  }

  const merged = deepMerge(sharedTranslations, override);
  mergedDictionaries[locale] = merged;
  return merged;
}

// Ensure the default locale is merged on startup
getDictionary(DEFAULT_LOCALE);

// --- Svelte Stores ---

/** Writable store holding the current locale code */
export const locale = writable<LocaleCode>(DEFAULT_LOCALE);

/**
 * Helper to update the locale. Use this instead of `locale.set` if you
 * want to ensure valid locales only.
 */
export function setLocale(newLocale: string) {
  if (SUPPORTED_LOCALES.includes(newLocale as LocaleCode)) {
    locale.set(newLocale as LocaleCode);
  } else {
    console.warn(
      `Unsupported locale: ${newLocale}, falling back to ${DEFAULT_LOCALE}`,
    );
    locale.set(DEFAULT_LOCALE);
  }
}

/**
 * Derived store providing the translation function `t(key, params)`.
 * It automatically updates when the `locale` store changes.
 *
 * @example
 * ```svelte
 * <script>
 *   import { t } from "$lib/i18n";
 * </script>
 * <h1>{$t("cv.about.title")}</h1>
 * ```
 */
function createTStore() {
  const initialLoc = get(locale) || DEFAULT_LOCALE;
  const initialDict = getDictionary(initialLoc);
  const { subscribe, set } = writable(
    (key: string, params?: Record<string, any>) => {
      return resolveKey(initialDict, key, params);
    },
  );

  locale.subscribe(($locale) => {
    const dict = getDictionary($locale || DEFAULT_LOCALE);
    set((key: string, params?: Record<string, any>) =>
      resolveKey(dict, key, params),
    );
  });

  return { subscribe };
}

export const t = createTStore();

function createTArrStore() {
  const initialLoc = get(locale) || DEFAULT_LOCALE;
  const initialDict = getDictionary(initialLoc);
  const { subscribe, set } = writable(
    (key: string, params?: Record<string, any>) => {
      return resolveKeyArr(initialDict, key, params);
    },
  );

  locale.subscribe(($locale) => {
    const dict = getDictionary($locale || DEFAULT_LOCALE);
    set((key: string, params?: Record<string, any>) =>
      resolveKeyArr(dict, key, params),
    );
  });

  return { subscribe };
}

export const tArr = createTArrStore();

// --- Server-side / Non-reactive access ---

/**
 * Resolves a translation key server-side or outside of Svelte components.
 *
 * @param key - The translation key path (e.g. "cv.about.title")
 * @param loc - The locale to resolve for (defaults to current store locale)
 * @param params - Optional variables for substitution
 */
export function getServerTranslation(
  key: string,
  loc?: LocaleCode,
  params?: Record<string, any>,
): string {
  const l = loc || get(locale);
  const dict = getDictionary(l);
  return resolveKey(dict, key, params);
}

/**
 * Resolves a translation key to an array server-side.
 */
export function getServerTranslationArr(
  key: string,
  loc?: LocaleCode,
  params?: Record<string, any>,
): string[] {
  const l = loc || get(locale);
  const dict = getDictionary(l);
  return resolveKeyArr(dict, key, params);
}

export function getLocalizedUrl(href: string, currentLocale: Language["code"]) {
  if (
    href.startsWith("http") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:")
  )
    return href;

  const isSupported = languages.some((l: Language) => l.code === currentLocale);
  if (!isSupported) return href;

  const supportedCodes = languages.map((l: Language) => l.code);
  let raw = href.replace(/\/+$/, "");
  if (!raw.startsWith("/")) raw = "/" + raw;
  const segments = raw.split("/");
  if (
    segments.length > 1 &&
    supportedCodes.includes(segments[1] as Language["code"])
  ) {
    segments.splice(1, 1);
    raw = segments.join("/");
  }

  const cleanHref = !raw || raw === "/" ? "" : raw;
  return `/${currentLocale}${cleanHref}`;
}

export function isActive(
  currentLocale: LocaleCode,
  itemUrl: string,
  itemUrl2?: string,
): boolean {
  const path = page.url.pathname.replace(/\/$/, "").concat("/") || "/";
  const localized1 =
    getLocalizedUrl(itemUrl, currentLocale).replace(/\/$/, "").concat("/") ||
    "/";
  const localized2 = itemUrl2
    ? getLocalizedUrl(itemUrl2, currentLocale).replace(/\/$/, "").concat("/") ||
      "/"
    : undefined;
  return (
    path === localized1 ||
    (!!localized2 && path === localized2) ||
    path === itemUrl ||
    (!!itemUrl2 && path === itemUrl2)
  );
}

export type { LocaleCode };
export { SUPPORTED_LOCALES, DEFAULT_LOCALE };
